import React from 'react';
import MediaBreakpoints from 'Utils/MediaBreakpoints';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { renderItemQty } from 'HelperFunctions/rentals';
import { Items } from 'Utils/SvgIcons';
import RentalBundleItem from './rental_bundle/RentalBundleItem';
import Imgix from 'react-imgix';
import CurrencyLabel from 'Utils/CurrencyLabel';
import { shouldHidePrice } from 'HelperFunctions/HidePricing';
import { formatSmartPrincingLabel } from 'HelperFunctions/smartPricing';
import CurrencyFormatter from 'HelperFunctions/CurrencyFormatter';
import { sanitizedData } from 'HelperFunctions/sanitizeData';
import ItemNotesModal from './ItemNotesModal';
import {
  DISCOUNT_DISPLAY_MODES,
  DISCOUNT_DISPLAY_DEFAULT,
} from 'Constants/discountDisplay';

class RentalBundle extends React.Component {
  // Value-only content helpers (return inner JSX, not a <td>) so the desktop
  // cells and the mobile labeled-lines share the EXACT same logic.
  priceContent() {
    const {
      rentalBundle,
      rental,
      location: { locale },
    } = this.props;
    const smartPricingActive = rental.smartPricingActive;
    const formatter = CurrencyFormatter({ locale });
    return (
      <span className='normalText'>
        <div>
          {smartPricingActive
            ? formatSmartPrincingLabel(rentalBundle, formatter)
            : rentalBundle.periodPrice}
        </div>
        {rentalBundle.bundle.purchasePrice && (
          <div>
            {' + '}
            <CurrencyLabel value={rentalBundle.bundle.purchasePrice} />
            {' / Each'}
          </div>
        )}
      </span>
    );
  }

  renderPrice() {
    return <td className='right'>{this.priceContent()}</td>;
  }

  feeContent() {
    const { rentalBundle } = this.props;
    const totalFees = parseFloat(rentalBundle.totalFees) || 0;
    return totalFees > 0 ? <CurrencyLabel value={totalFees} /> : null;
  }

  renderFeeCell() {
    return <td className='medSm right'>{this.feeContent()}</td>;
  }

  discountContent() {
    const { rentalBundle } = this.props;
    const pre = parseFloat(rentalBundle.selectedPrice);
    const post = parseFloat(rentalBundle.discountedPrice);
    const discount = !isNaN(pre) && !isNaN(post) ? pre - post : 0;
    return discount > 0 ? <CurrencyLabel value={-Math.abs(discount)} /> : null;
  }

  renderDiscountCell() {
    return <td className='medSm right'>{this.discountContent()}</td>;
  }

  // Subtotal = final_value (value + fees - discounts), matching the admin
  // tables. subtotal_strike struck value is value + fees (base_value +
  // total_fees). `inline` renders the struck value BESIDE the final value
  // (mobile lines); otherwise it stacks ABOVE (desktop columns).
  renderSubTotal(inline = false) {
    const {
      rentalBundle,
      storefrontShopSetting,
      approvalStatus,
      discountDisplay,
    } = this.props;

    if (
      approvalStatus !== 'approved' &&
      !storefrontShopSetting.shopShowItemPricing
    ) {
      return null;
    }
    if (!rentalBundle.selectedPrice && !rentalBundle.discountedPrice) {
      return null;
    }

    const mode = discountDisplay || DISCOUNT_DISPLAY_DEFAULT;
    const selectedPrice = parseFloat(rentalBundle.selectedPrice);
    const discountedPrice = parseFloat(rentalBundle.discountedPrice);
    const preDiscount = !isNaN(selectedPrice) ? selectedPrice : 0;
    const post = parseFloat(rentalBundle.finalValue) || 0;
    const rowHasDiscount =
      !isNaN(discountedPrice) && discountedPrice !== preDiscount;
    const struck =
      (parseFloat(rentalBundle.baseValue) || 0) +
      (parseFloat(rentalBundle.totalFees) || 0);

    if (mode === DISCOUNT_DISPLAY_MODES.SUMMARY_ONLY && rowHasDiscount) {
      return (
        <span>
          <CurrencyLabel value={post} />{' *'}
        </span>
      );
    }

    if (mode === DISCOUNT_DISPLAY_MODES.SUBTOTAL_STRIKE && rowHasDiscount) {
      return inline ? (
        <span>
          <span className='strikeThrough'>
            <CurrencyLabel value={struck} />
          </span>{' '}
          <CurrencyLabel value={post} />
        </span>
      ) : (
        <>
          <div className='strikeThrough'>
            <CurrencyLabel value={struck} />
          </div>
          <CurrencyLabel value={post} />
        </>
      );
    }

    return (
      <span>
        <CurrencyLabel value={post} />
      </span>
    );
  }

  render() {
    const {
      rentalBundle,
      showItemImages,
      showItemNotes,
      showUnitPricingToCustomers,
      tileView,
      approvalStatus,
      storefrontShopSetting,
      showFeesCol,
      showDiscountsCol,
    } = this.props;
    const { rentalItems, rentalAddOns } = rentalBundle;
    const bundle = rentalBundle.bundle;
    const bundleItems = [...rentalItems, ...rentalAddOns];
    const mobileImageUrl =
      bundle && bundle.pictures[0]
        ? bundle.pictures[0].imgixUrl || bundle.pictures[0].thumbnailUrl
        : null;
    const hasMobileImage = Boolean(
      tileView && showItemImages && mobileImageUrl
    );
    const hideSubtotalPricing = shouldHidePrice(
      approvalStatus,
      storefrontShopSetting
    );
    const itemNotesVisibleToCustomer = rentalBundle.itemNotes.filter(
      (itemNote) => itemNote.shouldIncludeNotesInRentals
    );
    // Mobile line values — reuse the same content helpers as the desktop cells,
    // with the same gates (showFeesCol/showDiscountsCol/pricing). Subtotal is
    // rendered inline (struck value beside the final) for the mobile lines.
    const pricingVisible =
      approvalStatus === 'approved' &&
      showUnitPricingToCustomers &&
      storefrontShopSetting.shopShowItemPricing;
    const rateLine = pricingVisible ? this.priceContent() : null;
    const feeLine = showFeesCol ? this.feeContent() : null;
    const discountLine = showDiscountsCol ? this.discountContent() : null;
    const subtotalLine = this.renderSubTotal(true);

    return (
      <MediaBreakpoints
        desktop={
          <>
            <tbody className='itemContent'>
            <tr
              className={classnames({
                bottom: showItemNotes && itemNotesVisibleToCustomer.length > 0,
              })}
            >
              {tileView && showItemImages && (
                <td rowSpan={'1'} className='image'>
                  <figure>
                    {bundle &&
                    bundle.pictures[0] &&
                    bundle.pictures[0].thumbnailUrl ? (
                      <Imgix
                        src={bundle.pictures[0].thumbnailUrl}
                        alt={rentalBundle.name}
                        width={95}
                      />
                    ) : (
                      <Items />
                    )}
                  </figure>
                </td>
              )}
              <td className='normalText'>
                {rentalBundle.name}
                {showItemNotes &&
                  itemNotesVisibleToCustomer?.map((itemNote, idx) => (
                    <span
                      key={itemNote.id ?? idx}
                      colSpan={showUnitPricingToCustomers ? '3' : '2'}
                      className='caption editedText'
                      style={{ display: 'block' }}
                      dangerouslySetInnerHTML={sanitizedData(itemNote.message)}
                    />
                  ))}
              </td>
              <td
                className={classnames({
                  right: true,
                  alert: rentalBundle.hasShortage,
                })}
              >
                {renderItemQty(rentalBundle)}
              </td>
              {approvalStatus === 'approved' &&
                showUnitPricingToCustomers &&
                storefrontShopSetting.shopShowItemPricing &&
                this.renderPrice()}
              {showFeesCol && this.renderFeeCell()}
              {showDiscountsCol && this.renderDiscountCell()}
              <td className='right'>{this.renderSubTotal()}</td>
            </tr>
            </tbody>
            {/* Sub-items render as sibling tbodies in the SAME parent grid table
                so their columns line up with the bundle row (the nested table
                used before computed its own widths and misaligned). */}
            {bundle.showItemsToCustomer &&
              bundleItems.map((bundleItem, index) => (
                <RentalBundleItem
                  key={index}
                  item={bundleItem}
                  bundle={bundle}
                  bundlePeriod={rentalBundle.period}
                  tileView={tileView}
                  hideSubtotalPricing={hideSubtotalPricing}
                  showUnitPricingToCustomers={showUnitPricingToCustomers}
                  showItemNotes={showItemNotes}
                  showImageCol={tileView && showItemImages}
                  showRateCol={
                    approvalStatus === 'approved' &&
                    showUnitPricingToCustomers &&
                    storefrontShopSetting.shopShowItemPricing
                  }
                  showFeesCol={showFeesCol}
                  showDiscountsCol={showDiscountsCol}
                />
              ))}
          </>
        }
        mobile={
          <tbody className='mobileItemContent'>
            {/* Mobile: only render an image when one exists, and show it
                full-width (no reserved space / placeholder when missing). */}
            {hasMobileImage && (
              <tr>
                <td>
                  <figure className='mobileFullImage'>
                    <Imgix
                      src={mobileImageUrl}
                      alt={rentalBundle.name}
                      width={600}
                    />
                  </figure>
                </td>
              </tr>
            )}
            <tr className={classnames({ noBorder: hasMobileImage })}>
              <td className={classnames({ alert: rentalBundle.hasShortage })}>
                {rentalBundle.name}
                <div className='caption lineDetails'>
                  <div>
                    <strong>Quantity</strong> {renderItemQty(rentalBundle)}
                  </div>
                  {rateLine != null && (
                    <div>
                      <strong>Rate</strong> {rateLine}
                    </div>
                  )}
                  {feeLine != null && (
                    <div>
                      <strong>Fees</strong> {feeLine}
                    </div>
                  )}
                  {discountLine != null && (
                    <div>
                      <strong>Discounts</strong> {discountLine}
                    </div>
                  )}
                  {subtotalLine != null && (
                    <div>
                      <strong>Subtotal</strong> {subtotalLine}
                    </div>
                  )}
                </div>
                {/* Rich-text notes live in a modal on mobile (TD1-10310). */}
                {showItemNotes && (
                  <ItemNotesModal
                    itemName={rentalBundle.name}
                    itemNotes={itemNotesVisibleToCustomer}
                  />
                )}
                {bundle.showItemsToCustomer && (
                  <table className='grid' style={{ marginLeft: '15px' }}>
                    {bundleItems.map((bundleItem, index) => (
                      <RentalBundleItem
                        key={index}
                        item={bundleItem}
                        bundle={bundle}
                        bundlePeriod={rentalBundle.period}
                        tileView={tileView}
                        hideSubtotalPricing={hideSubtotalPricing}
                        showUnitPricingToCustomers={showUnitPricingToCustomers}
                        showItemNotes={showItemNotes}
                      />
                    ))}
                  </table>
                )}
              </td>
            </tr>
          </tbody>
        }
      />
    );
  }
}
const mapStateToProps = (state) => {
  const { parsedStorefrontSettings, location } = state.locations;
  const storefrontShopSetting = parsedStorefrontSettings.storefrontShopSetting;

  return { storefrontShopSetting, location };
};

export default connect(mapStateToProps)(RentalBundle);
